import React, { useMemo } from 'react';
import classNames from 'classnames';

import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronDoubleLeft,
  mdiChevronDoubleRight,
} from '@mdi/js';

import { Select } from 'components/UIComponents/Form';
import { IconButton } from 'components/UIComponents/Button';

import 'react-bulma-components/lib/components/pagination/pagination.sass';
import './style.scss';

const getPageSizeOptions = text =>
  [10, 20, 30, 40, 50].map(size => ({
    label: `${size} ${text}`,
    value: size,
  }));

export const Pagination = ({
  totalPages,
  currentPage,
  disabled,
  delta = 2,
  className,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  totalRecords,
  hasToolbar = true,
  recordsPerPageText = 'kirjet lehel',
  recordsText = 'kirjet',
  pageText = 'lehekülge',
  renderPagesAs = 'select', // or buttons
}) => {
  const calculateFirstVisiblePage = lastPage => {
    if (currentPage === 1) return currentPage;
    const minPage = lastPage - delta * 2;
    const page = Math.min(currentPage - delta, minPage);

    return page <= 0 ? 1 : page;
  };

  const calculateLastVisiblePage = () => {
    if (currentPage === totalPages) return currentPage;
    const maxPage = delta * 2 + 1;
    const page = Math.max(currentPage + delta, maxPage);
    return page > totalPages ? totalPages : page;
  };

  const lastPage = calculateLastVisiblePage();
  const firstPage = calculateFirstVisiblePage(lastPage);

  const pageSizeOptions = useMemo(
    () => getPageSizeOptions(recordsPerPageText),
    [recordsPerPageText]
  );

  return (
    <div
      className={classNames(className, {
        pagination: true,
        'has-toolbar': hasToolbar,
      })}
    >
      {hasToolbar && (
        <div className="pagination-toolbar mr-5">
          <span className="mr-3">{`${totalRecords} ${recordsText}, ${totalPages} ${pageText}`}</span>
          <Select
            options={pageSizeOptions}
            value={{
              label: `${pageSize} ${recordsPerPageText}`,
              value: pageSize,
            }}
            onChange={({ value }) => onPageSizeChange(value)}
            ariaLabel="Pagination page size select"
            inputId="paginationPageSizeSelect"
          />
        </div>
      )}
      <ul className="pagination-list" aria-label="pagination">
        <li className="flex-center-y">
          <IconButton
            className="pagination-previous"
            path={mdiChevronDoubleLeft}
            onClick={() => onPageChange(1)}
            type="button"
            disabled={disabled || currentPage === 1}
            id="paginationFirstPageButton"
            aria-label="Pagination first page button"
          />
        </li>
        <li className="flex-center-y">
          <IconButton
            className="pagination-previous"
            path={mdiChevronLeft}
            onClick={() => onPageChange(currentPage - 1)}
            type="button"
            disabled={disabled || currentPage === 1}
            id="paginationPrevButton"
            aria-label="Pagination previous page button"
          />
        </li>
        {renderPagesAs === 'buttons' &&
          Array(lastPage - firstPage + 1)
            .fill()
            .map((_, idx) => {
              const page = firstPage + idx;
              return (
                <li key={'page' + page}>
                  <button
                    type="button"
                    className={classNames('pagination-link', {
                      'is-current': currentPage === page,
                    })}
                    aria-label={`Page ${page}`}
                    aria-current="page"
                    onClick={() => onPageChange(page)}
                    id={`pageSelectButton${page}`}
                  >
                    {page}
                  </button>
                </li>
              );
            })}
        {renderPagesAs === 'select' && (
          <li>
            <Select
              options={Array(totalPages)
                .fill()
                .map((_, idx) => ({
                  label: idx + 1,
                  value: idx + 1,
                }))}
              value={{
                label: currentPage,
                value: currentPage,
              }}
              onChange={({ value }) => onPageChange(value)}
              ariaLabel="Pagination page number select"
              inputId="paginationPageNumberSelect"
            />
          </li>
        )}
        <li className="flex-center-y">
          <IconButton
            className="pagination-next"
            path={mdiChevronRight}
            onClick={() => onPageChange(currentPage + 1)}
            type="button"
            disabled={disabled || currentPage === totalPages}
            id="paginationNextButton"
            aria-label="Pagination next page button"
          />
        </li>
        <li className="flex-center-y">
          <IconButton
            className="pagination-next"
            path={mdiChevronDoubleRight}
            onClick={() => onPageChange(totalPages)}
            type="button"
            disabled={disabled || currentPage === totalPages}
            id="paginationLastPageButton"
            aria-label="Pagination last page button"
          />
        </li>
      </ul>
    </div>
  );
};

export default Pagination;
